import { Command, env, promptMarker } from '@hackvegas-2025/shared';
import { State, log } from './state';
import { initWebsocket } from './websocket';
import { keyCaptureTerminalPrompt, terminalPrompt } from './input';

export const commands = {
  [Command.fullscreen]: {
    name: Command.fullscreen,
    execute() {
      const isFullScreen = document.fullscreenElement !== null;
      if (isFullScreen) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    },
  },
  [Command.tv]: {
    name: Command.tv,
    async execute(state) {
      if (!state.code) {
        log('Unauthorized');
        return;
      }

      // firefox doesn't support documentPictureInPicture
      if (!('documentPictureInPicture' in window)) {
        window.open(`${env.tvUrl}?code=${state.code}`, '_blank');
        return;
      }

      // todo: find official typings for documentPictureInPicture
      const pipWindow = await (window as any).documentPictureInPicture.requestWindow({ width: 1080, height: 720 });
      // todo: deploy the tv and have this open the correct hosted url
      pipWindow.document.body.style.margin = 0;
      pipWindow.document.body.style.transformOrigin = 'top left';
      pipWindow.document.body.style.transform = `scale(0.5)`;
      pipWindow.document.body.innerHTML = `<iframe src="${env.tvUrl}?code=${state.code}" style="position:fixed;inset:0;border:0;width:200vw;height:200vh;"></iframe>`;
      state.prompt = '';
    },
  },
  [Command.chat]: {
    name: Command.chat,
    description: 'Broadcast a system-wide message to connected terminals',
    async execute(state) {
      log('Enter chat message:');
      const [message, cancelled] = await terminalPrompt(state);
      if (cancelled) return;
      state.send({ command: Command.chat, message });
      state.prompt = '';
    },
  },
  [Command.boot]: {
    name: Command.boot,
    description: 'Submit boot signal to tv',
    execute(state) {
      state.send({ command: Command.boot });
    },
  },
  [Command.ssh]: {
    name: Command.ssh,
    description: 'Initiate secure shell link to another device',
    async execute(state) {
      log('Connected to network device: "TV"');
      log('All keystrokes will be transmitted to the connected device');
      log('ESC to exit session');
      for await (const key of keyCaptureTerminalPrompt(state)) {
        console.log(key);
        state.send({ command: Command.ssh });
      }
      log('SSH session closed');
    },
  },
  [Command.login]: {
    name: Command.login,
    description: 'Authenticate with an access code',
    async execute(state) {
      log('Access code:');
      const [code, cancelled] = await terminalPrompt(state);
      if (cancelled) return;
      log(`> ${'*'.repeat(code.length)}`);
      const { send, pollForSocketConnected } = initWebsocket(code);
      await pollForSocketConnected();
      state.send = send;
      state.prompt = '';
    },
  },
  // [Command.logout] -- might not need this. just refresh the page.
  [Command.color]: {
    name: Command.color,
    description: 'Cycle phosphor display tint',
    execute(state) {
      const currentIndex = state.availableColors.indexOf(state.color);
      const nextIndex = (currentIndex + 1) % state.availableColors.length;
      state.color = state.availableColors[nextIndex]!;
      log(`Terminal color is: ${state.color.toUpperCase()}`);
    },
  },
  [Command.clock]: {
    name: Command.clock,
    description: 'Report time remaining in current system phase',
    execute(state) {
      state.send({ command: Command.clock });
    },
  },
  [Command.clear]: {
    name: Command.clear,
    execute(state) {
      state.lines = [];
      state.renderLineTime = 0;
    },
  },
  [Command.help]: {
    name: Command.help,
    execute(state) {
      const cmds = state.availableCommands.map((cmd) => commands[cmd]);
      const commandsWithDescription = cmds.filter((cmd) => 'description' in cmd);
      const otherCommands = cmds.filter((cmd) => !('description' in cmd));
      log(`Commands:

${commandsWithDescription.map((cmd) => `  ${cmd.name}${' '.repeat(20 - cmd.name.length)}${cmd.description}`).join('\n')}

More commands:        ${otherCommands.map((cmd) => cmd.name).join(', ')}
`);
    },
  },
  [Command.info]: {
    name: Command.info,
    execute() {
      log(`███████████████████████████████████████████████████████████████████████

                    %%GLITCH(HACKVEGAS : SYSTEM INFO)%%

███████████████████████████████████████████████████████████████████████
%%WAIT(500)%%
%%TYPING($ info HACKATHON.sys)%%                          %%LOADING(1000)%%
HOST_ENV /home/austin
> ingress / egress...........................................[  OK  ]
> interrupt codes..................[SIGCLIMB SIGUBEREATS SIGPINGPONG] %%WAIT(500)%%

%%TYPING($ cat ./HACKATHON.cron)%%                        %%LOADING(300)%%
0 18 17 10 *   /usr/bin/welcome.sh                 # Fri Oct 17 18:00
0 15 19 10 *   /usr/bin/deadline.sh                # Sun Oct 19 15:00
30 15 19 10 *  /usr/bin/demos.sh                   # Sun Oct 19 15:30 %%WAIT(500)%%

%%TYPING($ info ./PROJECT_FILES)%%                        %%LOADING(700)%%
> working solo or in teams...................................[  OK  ]
> ai, engines, libraries.....................................[  OK  ]
> prebuilt final assets......................................[%%FLASHTEXT(DENIED)%%]

%%TYPING($ echo $THEME)%%                                %%LOADING(2000)%%
  > THEME 2025: "%%GLITCH(CRT Nights: Hack Like It's 1989)%%"
    > Old-school terminals, 8-bit sound + pixel art
    > Arcade games (Galaga, Street Fighter, Robotron)
    > The phosphor glow of a CRT display

%%TYPING($ echo $PRIZES)%%                              %%LOADING(2000)%%
  > %%GLITCH(/dev/floppy)%%
  > %%GLITCH($50 Amazon gift card)%%`);
    },
  },
} satisfies Record<
  Command,
  {
    name: Command;
    description?: string;
    execute: (state: State) => void;
  }
>;
